import { GripVertical, Mail, Phone, FileText, Calendar, AlertTriangle, Briefcase, Building } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import type { Lead } from "@/types/lead";
import { isHumanHandoffVisualStage, type LeadStageConfig } from "@/lib/lead-stage-agent-handoff";

interface GroupLeadCardProps {
  membershipId: string;
  lead: Lead;
  role?: string;
  department?: string;
  onClick: () => void;
  handoffStagesMap?: Map<string, LeadStageConfig>;
}

export function GroupLeadCard({
  membershipId,
  lead,
  role,
  department,
  onClick,
  handoffStagesMap,
}: GroupLeadCardProps) {
  const isUrgentHandoff = isHumanHandoffVisualStage(lead.stage, handoffStagesMap);
  return (
    <div
      className={cn(
        "group p-3 border rounded-lg bg-card cursor-pointer hover:bg-muted/50 transition-colors",
        isUrgentHandoff && "ring-2 ring-orange-500 bg-orange-50 dark:bg-orange-950/20"
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-2">
        <div className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors mt-0.5">
          <GripVertical className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2">
            <p className="font-medium truncate flex-1">{lead.name}</p>
            {isUrgentHandoff && (
              <Badge variant="destructive" className="text-xs shrink-0">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Urgente
              </Badge>
            )}
            {lead.current_appointment_id && lead.current_appointment_date && (
              <Badge variant="secondary" className="text-xs shrink-0">
                <Calendar className="w-3 h-3 mr-1" />
                {new Date(lead.current_appointment_date).toLocaleDateString('pt-BR', { 
                  day: '2-digit', 
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Badge>
            )}
          </div>

          {(role || department) && (
            <div className="flex flex-wrap gap-1">
              {role && (
                <Badge variant="outline" className="text-xs">
                  <Briefcase className="w-3 h-3 mr-1" />
                  {role}
                </Badge>
              )}
              {department && (
                <Badge variant="secondary" className="text-xs">
                  <Building className="w-3 h-3 mr-1" />
                  {department}
                </Badge>
              )}
            </div>
          )}
          
          {lead.email && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Mail className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{lead.email}</span>
            </div>
          )}
          
          {lead.phone && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Phone className="w-3 h-3 flex-shrink-0" />
              <span>{lead.phone}</span>
            </div>
          )}
          
          {lead.notes && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <FileText className="w-3 h-3 flex-shrink-0" />
              <span className="line-clamp-1">{lead.notes}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
